import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './css/NewPred.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX } from '@fortawesome/free-solid-svg-icons';
import ludA from './images/step1a.png';
import ludB from './images/step1b.png';
import KetcherComp from './KetcherComp.js';
import labService from './labService';

const NewPred = () => {
    const navigate = useNavigate();
    const { analysisPk } = useParams();
    const [analName, setAnalName] = useState("");
    const [smiles, setSmiles] = useState("");
    const [haveSmiles, setHaveSmiles] = useState('dopa');
    const [error, setError] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [validSmiles, setValidSmiles] = useState(true);
    const [analysis, setAnalysis] = useState(null);
    const npContentRef = useRef(null);

    useEffect(() => {
        if (!analysisPk) return;

        const user = JSON.parse(localStorage.getItem('user'));

        if (!user) {
            setError("You must be logged in to view this page.");
            return;
        }

        const fetchAnalIfPk = async () => {
            try {
                const response = await labService.getAnalysisDetail(analysisPk);
                console.log(response.data);
                setAnalysis(response.data);
                setSmiles(response.data.smiles);
                setAnalName(response.data.name);

            } catch (error) {
                console.error('Error fetching analysis details:', error);
                setError('Failed to fetch Analysis. Please try again.');
            }
        };
        
        fetchAnalIfPk();
    }, [analysisPk]);

    const handleUpdate = async () => {
        const currentSmiles = await window.ketcher.getSmiles();
        setSmiles(currentSmiles);
        setError(null); // Clear previous errors when updating
    };

    const handleGoBack = () => {
        navigate('/home');
    };

    const handleBackendError = (err) => {
        if (err.response && err.response.data && err.response.data.error) {
            const backendError = err.response.data.error;
            
            if (backendError === 'Invalid_Smiles') {
                setValidSmiles(false);
                setError(null); 
            } else if (backendError === 'model_ionic_smiles_general') {
                setValidSmiles(true);
                setError("The molecular structure you have defined is neutral or does not compose of anion and cation, however the model(s) were specifically trained on ionic liquids, thus the prediction cannot be proceeded.");
            } else if (backendError === 'model_general_smiles_ionic') {
                setValidSmiles(true);
                setError("The molecular structure you have defined is ionic, however the model(s) were specifically trained for neutral chemicals, thus the prediction cannot be proceeded.");
            } else {
                setValidSmiles(true);
                setError("An error occurred while starting the prediction. Please try again.");
            }
        } else {
            setValidSmiles(true);
            setError("A network error occurred. Please check your connection and try again.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitted(true);
        setError(null); 
    
        if (!analName || !smiles) {
            return;
        }
    
        const user = JSON.parse(localStorage.getItem('user'));
        
        if (!user || !user.access) {
            setError("You must be logged in to start a prediction.");
            return;
        }
    
        if (!analysisPk) {
            const endpoint_type = JSON.parse(localStorage.getItem('selectedEndpoint'));
            const selected_models = JSON.parse(localStorage.getItem('selectedModels'));
        
            const data = {
                smiles: smiles,
                name: analName,
                endpoint_type: endpoint_type,
                selected_models: selected_models,
            };
        
            try {
                const response = await labService.startPrediction(data);
                if (response && response.data && response.data.redirect_url) {
                    navigate(response.data.redirect_url);
                } else {
                    setError("An error occurred while starting the prediction. Please try again.");
                }
            } catch (err) {
                handleBackendError(err);
            }

        } else {
            const selected_models = JSON.parse(localStorage.getItem('selectedModels'));
            const data = {
                analysis_pk: analysisPk,
                smiles: smiles,
                name: analName,
                selected_models: selected_models,
            };

            try {
                const response = await labService.startPrediction(data);
                if (response && response.data && response.data.redirect_url) {
                    navigate(response.data.redirect_url);
                } else {
                    setError("An error occurred while starting the prediction. Please try again.");
                }
            } catch (err) {
                handleBackendError(err);
            }
        }
    };

    return (
        <div className='new-pred-container'>
            <div className='np-tabs'>
                <div className='x'>
                    <button onClick={handleGoBack} className='x-but'>
                        <FontAwesomeIcon className='icon-x' icon={faX} />
                    </button>
                </div>
                <div className='np-steps done-step'>
                    <p className='p-step'>STEP 1</p>
                    <p>Select Model</p>
                </div>
                <div className='np-steps active-tab'>
                    <p className='p-step'>STEP 2</p>
                    <p>Define Compound</p>
                </div>
                <div className='np-steps'>
                    <p className='p-step'>STEP 3</p>
                    <p>Summary</p>
                </div>
                <div className='np-steps'>
                    <p className='p-step'>STEP 4</p>
                    <p>Results</p>
                </div>
            </div>
            <div className='np-content' ref={npContentRef}>
                <form className='anal-form'>
                    <div className='accessibility-info'>
                        <p>
                            Your structure remains accessible only to you. It is stored on our server only once the analysis has been completed. It is removed from our server when you delete the analysis from the "Laboratory" panel.
                        </p>
                    </div>
                    <div className='input-wrap'>
                        <div className='input-cont'>
                            <label className={`${isSubmitted && !analName ? 'error-input' : ''}`} htmlFor="anal-name">Analysis name</label>
                            <input
                                className='input-anal-name'
                                type="text"
                                id="anal-name"
                                value={analName}
                                placeholder='max. 65 characters'
                                onChange={(e) => setAnalName(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                    <div className='anal-error-div'>
                        <p className={`${isSubmitted && !analName ? 'anal-error' : 'anal-error-hide'}`}>Analysis name is required</p>
                    </div>
                    <div className='form-options'>
                        <div className={`form-opt ${haveSmiles === 'yes' ? 'active-opt' : ''}`}
                            onClick={() => setHaveSmiles('yes')}>
                            <div className='lud-smi-div'>
                                <img className="ludzik-smiles" src={ludB} alt="ludzik" />
                            </div>
                            <p>I have SMILES notation for my compound</p>
                        </div>
                        <div className='or'>
                            <p>OR</p>
                        </div>
                        <div className={`form-opt ${haveSmiles === 'no' ? 'active-opt' : ''}`}
                            onClick={() => setHaveSmiles('no')}>
                            <div className='lud-smi-div'>
                                <img className="ludzik-smiles" src={ludA} alt="ludzik" />
                            </div>
                            <p>I don't have SMILES notation for my compound</p>
                        </div>
                    </div>

                    {haveSmiles === 'yes' ? (
                    <div className='one-hun'>
                        <div className='input-wrap'>
                            <div className='input-cont'>
                                <label className={`${isSubmitted && (!smiles || !validSmiles || error) ? 'error-input' : ''}`} htmlFor="smiles">Compound SMILES notation:</label>
                                <input
                                    className='input-text'
                                    type="text"
                                    id="smiles"
                                    value={smiles}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        setSmiles(inputValue);
                                        if (inputValue.trim() === "") {
                                            setValidSmiles(true);
                                        }
                                        setError(null);
                                    }}
                                    required
                                />
                            </div>
                        </div>
                        {/* Errors for "I have SMILES" option */}
                        <div className='anal-error-div'>
                            <p className={`${isSubmitted && !validSmiles && smiles ? 'smiles-error' : 'anal-error-hide'}`}>The SMILES notation you have provided could not be translated to molecular structure. Try again.</p>
                            <p className={`${error ? 'smiles-error' : 'anal-error-hide'}`}>{error}</p>
                        </div>
                    </div>
                        
                    ) : (haveSmiles === 'no' ? (
                        <div className='input-ketcher'>
                            <label className={`label-ketcher ${isSubmitted && (!smiles || !validSmiles || error) ? 'error-input' : ''}`} htmlFor="smiles">Compound SMILES notation:</label>
                            <div className='input-updater'>
                                <input
                                    className='input-text'
                                    type="text"
                                    id="smiles"
                                    value={smiles}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        setSmiles(inputValue);
                                        if (inputValue.trim() === "") {
                                            setValidSmiles(true);
                                        }
                                        setError(null);
                                    }}
                                    required
                                />
                                <button type="button" onClick={handleUpdate}>Update</button>
                                
                            </div>
                            {/* Errors for "I don't have SMILES" option */}
                            <div className='smiles-error-div'>
                                <p className={`${isSubmitted && !validSmiles && smiles ? 'smiles-error' : 'anal-error-hide'}`}>The SMILES notation you have provided could not be translated to molecular structure. Try again.</p>
                                <p className={`${error ? 'smiles-error' : 'anal-error-hide'}`}>{error}</p>
                            </div>
                            <p className='p-draw-ketch'>Draw a structure and SMILES notation will generate after clicking <span className='bold'>Update button</span></p>
                        </div>
                    ) : null)}  
                </form>

                {haveSmiles === 'yes' ? (
                <div className='center-items'>
                    <button className={`${smiles && analName ? 'save-but-ready' : 'save-but'}`} type="submit" onClick={handleSubmit}>Next</button>
                </div>
                ) : (haveSmiles === 'no' ? (
                <div className='center-items'>
                    <div className='div-ketcher'>
                        <div className='ketcher-wrap'>
                            <KetcherComp />
                        </div>
                        <button className={`${smiles && analName ? 'save-but-ready' : 'save-but'}`} type="submit" onClick={handleSubmit}>Next</button>
                    </div>
                </div>
                ) : null)} 
            </div>
        </div>
    );
};

export default NewPred;